import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styles from './DashboardHeader.module.scss'
import { Search, X, SendHorizonal, Loader2, Info, Users, Gavel, Briefcase, FileText } from 'lucide-react'
import {
  getDesktopSuggestions,
  getMobileResponses,
  normalizeText,
  keywordMatches,
  type DesktopSuggestion,
  type MobileResponse
} from './dashboardActionsConfig.tsx'
import useNavVisibility from '../../../hooks/useNavVisibility.ts'
import { useDispatch, useSelector } from 'react-redux'
import { getTotalUsers, getTotalPublicProjects, getAllWorkOffers, getTotalPublicQuotations, getTotalPrivateQuotations } from '../../../store/actions'

const formatStatValue = (value: number): string => {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}m`
  }
  if (value >= 1000) {
    const formatted = value / 1000
    return `${formatted % 1 === 0 ? formatted.toFixed(0) : formatted.toFixed(1)}k`
  }
  return value.toString()
}

const formatStatValueNoAbbrev = (value: number): string => {
  return value.toLocaleString('es-AR')
}

const MOBILE_PRIORITY_IDS = ['cotizar-materiales', 'ofrecer-servicios', 'publicar-oferta-trabajo']

export interface MobileResultsPayload {
  status: 'idle' | 'loading' | 'result' | 'empty'
  label?: string
  summary?: string
  cta?: () => void
}

interface DashboardHeaderProps {
  userName: string
  userId?: string
  userData?: any
  onMobileResultsChange?: (payload: MobileResultsPayload) => void
}

const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  userName,
  userId,
  userData,
  onMobileResultsChange
}) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [submittedQuery, setSubmittedQuery] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [matchedSuggestion, setMatchedSuggestion] = useState<DesktopSuggestion | null>(null)
  const [showMobileSuggestions, setShowMobileSuggestions] = useState(false)
  const [showLeftFade, setShowLeftFade] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const navigate = useNavigate()
  const dispatch = useDispatch<any>()
  const totalUsers = useSelector((state: any) => state.dashboard?.totalUsers ?? 0)
  const totalPublicProjects = useSelector((state: any) => state.dashboard?.totalPublicProjects ?? 0)
  const totalJobOffers = useSelector((state: any) => state.register?.allJobRequests?.total ?? 0)
  const totalPublicQuotations = useSelector((state: any) => state.dashboard?.totalPublicQuotations ?? 0)
  const totalPrivateQuotations = useSelector((state: any) => state.dashboard?.totalPrivateQuotations ?? 0)
  const [currentStatIndex, setCurrentStatIndex] = useState(0)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const { ocultarEnMobile } = useNavVisibility()
  const isMobile = ocultarEnMobile
  const mobileSuggestionsRef = useRef<HTMLDivElement | null>(null)
  const desktopSuggestions = useMemo(() => getDesktopSuggestions(navigate, userId, userData), [navigate, userId, userData])
  const mobileResponses = useMemo(() => getMobileResponses(navigate, userId, userData), [navigate, userId, userData])
  const mobileQuickSuggestions = useMemo(() => {
    const prioritized = MOBILE_PRIORITY_IDS
      .map(id => mobileResponses.find(suggestion => suggestion.id === id))
      .filter((suggestion): suggestion is MobileResponse => Boolean(suggestion))
    const remaining = mobileResponses.filter(suggestion => !MOBILE_PRIORITY_IDS.includes(suggestion.id))
    return [...prioritized, ...remaining].slice(0, 4)
  }, [mobileResponses])
  const totalQuotations = useMemo(() => {
    const publicQuot = typeof totalPublicQuotations === 'number' ? totalPublicQuotations : 0
    const privateQuot = typeof totalPrivateQuotations === 'number' ? totalPrivateQuotations : 0
    return publicQuot + privateQuot
  }, [totalPublicQuotations, totalPrivateQuotations])

  const activityStats = useMemo(
    () => [
      {
        id: 'usuarios-registrados',
        value: formatStatValueNoAbbrev(totalUsers),
        label: 'Usuarios verificados registrados',
        icon: <Users size={16} />
      },
      {
        id: 'licitaciones',
        value: formatStatValue(totalPublicProjects),
        label: 'Licitaciones abiertas hoy',
        icon: <Gavel size={16} />
      },
      {
        id: 'ofertas-laborales',
        value: formatStatValue(totalJobOffers),
        label: 'Ofertas de trabajo vigentes',
        icon: <Briefcase size={16} />
      },
      {
        id: 'cotizaciones',
        value: formatStatValue(totalQuotations),
        label: 'Cotizaciones totales',
        icon: <FileText size={16} />
      }
    ],
    [totalUsers, totalPublicProjects, totalJobOffers, totalQuotations]
  )

  useEffect(() => {
    if (!isMobile) {
      setShowMobileSuggestions(false)
      setShowLeftFade(false)
      return
    }

    const timeout = window.setTimeout(() => {
      setShowMobileSuggestions(true)
    }, 800)

    return () => window.clearTimeout(timeout)
  }, [isMobile])

  useEffect(() => {
    if (!showMobileSuggestions) {
      return
    }

    const element = mobileSuggestionsRef.current
    if (!element) {
      return
    }

    const updateFade = () => {
      setShowLeftFade(element.scrollLeft > 4)
    }

    updateFade()
    element.addEventListener('scroll', updateFade, { passive: true })

    return () => {
      element.removeEventListener('scroll', updateFade)
    }
  }, [showMobileSuggestions])

  useEffect(() => {
    dispatch(getTotalUsers())
    dispatch(getTotalPublicProjects())
    dispatch(getAllWorkOffers())
    dispatch(getTotalPublicQuotations())
    dispatch(getTotalPrivateQuotations())
  }, [dispatch])

  useEffect(() => {
    if (activityStats.length === 0) return

    const interval = setInterval(() => {
      setIsTransitioning(true)
      setTimeout(() => {
        setCurrentStatIndex((prevIndex) => (prevIndex + 1) % activityStats.length)
        setTimeout(() => {
          setIsTransitioning(false)
        }, 50)
      }, 300)
    }, 5000)

    return () => clearInterval(interval)
  }, [activityStats.length])

  const visibleStats = useMemo(() => {
    if (activityStats.length === 0) return []
    const stats = []
    for (let i = 0; i < 3; i++) {
      const index = (currentStatIndex + i) % activityStats.length
      stats.push({ ...activityStats[index], originalIndex: index })
    }
    return stats
  }, [activityStats, currentStatIndex])

  const getGreeting = () => {
    const hour = new Date().getHours()
    if (hour >= 6 && hour < 12) return "Buenos días"
    if (hour >= 12 && hour < 20) return "Buenas tardes"
    return "Buenas noches"
  }

  const normalizedQuery = normalizeText(searchQuery).trim()
  const hasTypedQuery = normalizedQuery.length > 0
  const shouldShowDropdown = submittedQuery.length > 0 || isProcessing
  const shouldShowActivityPulse = true

  const handleClearSearch = () => {
    setSearchQuery('')
    setSubmittedQuery('')
    setMatchedSuggestion(null)
    setIsProcessing(false)
    setIsFocused(false)
    if (isMobile) {
      onMobileResultsChange?.({ status: 'idle' })
    }
  }

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    if (!hasTypedQuery || isProcessing) {
      return
    }

    setIsProcessing(true)
    if (isMobile) {
      if (showMobileSuggestions) {
        setShowMobileSuggestions(false)
      }
      onMobileResultsChange?.({ status: 'loading' })
    }
    setMatchedSuggestion(null)
    setSubmittedQuery(searchQuery)

    const suggestionPool: (DesktopSuggestion | MobileResponse)[] = isMobile ? mobileResponses : desktopSuggestions
    const suggestionMatch = suggestionPool.find(config => keywordMatches(normalizedQuery, config.keywords))

    setTimeout(() => {
      if (!isMobile) {
        setMatchedSuggestion((suggestionMatch as DesktopSuggestion) ?? null)
      }
      setIsProcessing(false)
      if (isMobile) {
        if (suggestionMatch) {
          onMobileResultsChange?.({
            status: 'result',
            label: suggestionMatch.label,
            summary: (suggestionMatch as MobileResponse).summary,
            cta: suggestionMatch.action
          })
        } else {
          onMobileResultsChange?.({ status: 'empty' })
        }
      }
    }, 900)
  }

  const handleSuggestionAction = () => {
    if (!matchedSuggestion) {
      return
    }

    matchedSuggestion.action()
    setSearchQuery('')
    setSubmittedQuery('')
    setMatchedSuggestion(null)
    setIsFocused(false)
    if (isMobile) {
      onMobileResultsChange?.({ status: 'idle' })
    }
  }

  return (
    <div className={styles.header}>
      <div className={styles.topRow}>
        <div className={styles.greetingSection}>
          <h1 className={styles.greeting}>
            {getGreeting()}, <span className={styles.userName}>{userName}</span>
          </h1>
          <p className={styles.subtitle}>¿Qué necesitas hacer hoy?</p>
          {shouldShowActivityPulse && visibleStats.length > 0 && (
            <div className={`${styles.activityPulse} ${isTransitioning ? styles.activityPulseFadeOut : styles.activityPulseFadeIn}`}>
              {visibleStats.map((stat, index) => (
                <React.Fragment key={`${stat.id}-${stat.originalIndex}`}>
                  <div className={styles.activityItem}>
                    <span className={styles.activityIcon}>{stat.icon}</span>
                    <span className={styles.activityValue}>{stat.value}</span>
                    <span className={styles.activityLabel}>{stat.label}</span>
                  </div>
                  {index < visibleStats.length - 1 && <span className={styles.activityDivider}>•</span>}
                </React.Fragment>
              ))}
            </div>
          )}

        </div>
      </div>

      {isMobile && (
        <div
          className={`${styles.mobileSuggestions} ${showMobileSuggestions ? styles.mobileSuggestionsVisible : ''}`}
        >
          <div
            className={`${styles.mobileSuggestionsViewport} ${
              showLeftFade ? styles.mobileSuggestionsViewportLeftFade : ''
            }`}
          >
            <div className={styles.mobileSuggestionList} ref={mobileSuggestionsRef}>
              {mobileQuickSuggestions.map((suggestion) => (
                <button
                  key={suggestion.id}
                  type="button"
                  className={styles.mobileSuggestionCard}
                  onClick={() => suggestion.action()}
                >
                  {suggestion.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      <div className={styles.searchSection}>
        <form className={styles.searchForm} onSubmit={handleSubmit}>
          <div className={`${styles.searchBox} ${isFocused ? styles.searchBoxFocused : ''}`}>
            <Search size={20} className={styles.searchIcon} />
            <input
              type="text"
              className={styles.searchInput}
              placeholder={isMobile ? '' : 'Contanos qué querés hacer y te ayudamos...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setTimeout(() => setIsFocused(false), 200)}
              disabled={isProcessing}
            />
            {searchQuery && !isProcessing && (
              <button
                className={styles.clearButton}
                onClick={handleClearSearch}
                type="button"
              >
                <X size={16} />
              </button>
            )}
            <button
              type="submit"
              className={styles.sendIconButton}
              disabled={!hasTypedQuery || isProcessing}
            >
              <SendHorizonal size={18} />
            </button>
          </div>
        </form>


        {shouldShowDropdown && !isMobile && (
          <div className={styles.suggestionsDropdown}>
            <div className={styles.suggestionsContent}>
              {isProcessing && (
                <div className={styles.agentThinking}>
                  <Loader2 size={20} className={styles.agentThinkingIcon} />
                  <p>Analizando tu pedido...</p>
                </div>
              )}

              {!isProcessing && matchedSuggestion && submittedQuery && (
                <div className={styles.guideSuggestionItemContainer}>
                  <span className={styles.suggestionIcon}>TE PUEDE SERVIR</span>
                <button type="button" className={styles.guideSuggestionItem} onClick={handleSuggestionAction}>
                  <span className={styles.suggestionIcon}>{matchedSuggestion.icon}</span>
                  <span className={styles.suggestionText}>{matchedSuggestion.label}</span>
                  <span className={styles.guideSuggestionCta}>Abrir</span>
                </button>
                </div>
              )}

              {!isProcessing && !matchedSuggestion && submittedQuery && (
                <div className={styles.agentFallbackContainer}>
                  <span className={styles.agentFallbackTitle}>NO PUDIMOS ENCONTRAR NADA</span>
                <div className={styles.agentFallback}>
                  <Info size={16} className={styles.agentFallbackIcon} />
                  <span>Probá con “publicar obra”, “cotizar”, “licitaciones” o “mis proyectos”.</span>
                </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default DashboardHeader

